import React, {
  useRef,
  useState,
  useEffect,
} from 'react';
import {
  Modifier,
  RichUtils,
  Editor as DraftEditor,
  EditorState,
  convertToRaw,
  convertFromRaw,
  getDefaultKeyBinding,
} from 'draft-js';
import {
  markdownToDraft,
} from 'markdown-draft-js';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreSelectors,
} from '@gymcloud/core';
import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';

import {
  FileUploader,
} from 'web-components';

import draftToMarkdown from './draft-to-markdown';
import linkDecorator from './Decorator';

import {
  InlineStyleControls,
  BlockStyleControls,
} from './Controls';
import * as S from './styled';

const getInitialState = (source: string) => {
  if (!source) {
    return EditorState.createEmpty(linkDecorator);
  }

  const rawData = markdownToDraft(source, { preserveNewlines: true });

  return EditorState.createWithContent(
    convertFromRaw(rawData),
    linkDecorator,
  );
};

const addLinkFn = (editorState: EditorState, text: string, url: string) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  let currentContentWithEntity = currentContent.createEntity(
    'LINK',
    'MUTABLE',
    { url },
  );

  const entityKey = currentContentWithEntity.getLastCreatedEntityKey();

  if (selection.isCollapsed()) {
    currentContentWithEntity = Modifier.insertText(
      currentContentWithEntity,
      selection,
      text,
      undefined,
      entityKey,
    );
  }

  const nextEditorState = EditorState.set(
    editorState,
    {
      currentContent: currentContentWithEntity,
    },
  );

  return RichUtils.toggleLink(
    nextEditorState,
    selection,
    entityKey,
  );
};

const mapState = state => ({
  items: coreSelectors.getSelectionResourceMappedList(
    'documents',
    'library',
  )(state),
  ...getUiState([
    'fileUpload',
    'uploadFileModal',
  ])(state),
});

type Props = {
  id: number;
  source: string;
  minHeight?: string;
  isInline?: boolean;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
  onFocus: { (): void };
  onBlur: { (): void };
  onChange: { (markdown: string): void };
};

const Editor = ({
  id,
  source,
  isInline,
  minHeight,
  autoFocus,
  className,
  placeholder,
  onFocus,
  onBlur,
  onChange,
}: Props) => {
  const editor = useRef(null);
  const [editorState, setEditorState] = useState(
    getInitialState(source),
  );
  const [hasFocus, setFocus] = useState(false);
  const { fileUpload, uploadFileModal } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    if (autoFocus) {
      editor.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (id) {
      setEditorState(
        getInitialState(source),
      );
    }
  }, [id]);

  const toggleInlineStyle = (inlineStyle: string) => {
    setEditorState(
      RichUtils.toggleInlineStyle(
        editorState,
        inlineStyle,
      ),
    );
  };

  const toggleBlockType = (blockType: string) => {
    setEditorState(
      RichUtils.toggleBlockType(
        editorState,
        blockType,
      ),
    );
  };

  const handlePullBack = (data) => {
    if (onChange) {
      const contentState = data.getCurrentContent();
      const rawData = convertToRaw(contentState);
      const markdownStr = draftToMarkdown(rawData, { preserveNewlines: true });
      if (markdownStr !== source) {
        onChange(markdownStr);
      }
    }
  };

  const handleChange = (newEditorState: EditorState) => {
    if (!isInline) {
      handlePullBack(newEditorState);
    }
    setEditorState(newEditorState);
  };

  const keyBindingFn = (e) => {
    const keyCode = e.keyCode || e.which;
    // Tab
    if (keyCode === 9) {
      e.preventDefault();
      const currentBlockType = RichUtils.getCurrentBlockType(editorState);
      if (currentBlockType === 'ordered-list-item' || currentBlockType === 'unordered-list-item') {
        setEditorState(
          RichUtils.onTab(e, editorState, 6),
        );
      } else {
        toggleBlockType(
          'unordered-list-item',
        );
      }
    }

    // Enter
    if (isInline && keyCode === 13) {
      e.preventDefault();
      handlePullBack(editorState);
    }

    return getDefaultKeyBinding(e);
  };

  const handleKeyCommand = (command, eState) => {
    const newState = RichUtils.handleKeyCommand(eState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleAddDocument = (item: any) => {
    const newEditorState = addLinkFn(editorState, item.name, `document:${item.id}`);
    handleChange(newEditorState);
  };

  let hidePlaceholder = false;
  const contentState = editorState.getCurrentContent();
  if (!contentState.hasText()) {
    if (
      contentState
        .getBlockMap()
        .first()
        .getType() !== 'unstyled'
    ) {
      hidePlaceholder = true;
    }
  }

  return (
    <S.RichEditor
      minHeight={minHeight}
      hasFocus={hasFocus}
      className={className}
      hidePlaceholder={hidePlaceholder}
    >
      <DraftEditor
        ref={editor}
        placeholder={placeholder}
        editorState={editorState}
        onBlur={(e) => {
          setFocus(false);
          if (onBlur) onBlur(e);
          if (isInline) handlePullBack(editorState);
        }}
        onFocus={(e) => {
          setFocus(true);
          if (onFocus) onFocus(e);
        }}
        onChange={handleChange}
        keyBindingFn={keyBindingFn}
        handleKeyCommand={handleKeyCommand}
      />

      {hasFocus && (
        <S.Controls>
          <InlineStyleControls
            editorState={editorState}
            onToggle={toggleInlineStyle}
          />
          <span>|</span>
          <BlockStyleControls
            editorState={editorState}
            onLink={handleAddDocument}
            onToggle={toggleBlockType}
          />
        </S.Controls>
      )}

      <FileUploader
        title="Upload documents"
        isOpen={uploadFileModal.isOpen}
        isLoaded={uploadFileModal.isLoaded}
        accept="application/pdf"
        fileUpload={fileUpload}
        resourceType="documents"
        placeholder={(
          <span>
            Drop your
            {' '}
            <b>PDF</b>
            {' '}
            files here or
          </span>
        )}
        onRequestDrop={(files) => {
          dispatch(
            uiActions.requestUploadDocuments({
              files,
            }),
          );
        }}
        onRequestClose={() => {
          // add the first upoaded document
          const loaded = Object.keys(fileUpload);
          if (uploadFileModal.isLoaded && loaded.length !== 0) {
            handleAddDocument(fileUpload[loaded[0]]);
          }

          dispatch(
            uiActions.setUiState({
              fileUpload: {},
              uploadFileModal: {
                isOpen: false,
              },
            }),
          );
        }}
        onRequestCancel={() => {
          dispatch(uiActions.cancelUploadFiles());
        }}
      />
    </S.RichEditor>
  );
};

Editor.defaultProps = {
  isInline: false,
  autoFocus: false,
  className: '',
  placeholder: '',
  minHeight: '40px',
};

export default Editor;
